import { fromCognitoIdentityPool as FromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';

import { routes } from 'data/routes';

const UserPoolId = process.env.REACT_APP_AWS_USER_POOL_ID;
const ClientId = process.env.REACT_APP_AWS_CLIENT_ID;
const region = process.env.REACT_APP_AWS_REGION;
const identityPoolId = process.env.REACT_APP_AWS_IDENTITY_POOL_ID;
const identityProvider = `cognito-idp.${region}.amazonaws.com/${UserPoolId}`;

const poolData = {
  UserPoolId,
  ClientId,
};

const userPool =
  poolData.ClientId && poolData.UserPoolId ? new CognitoUserPool(poolData) : undefined;

let currentUser = userPool?.getCurrentUser();

export async function getSession() {
  if (!currentUser) {
    return false;
  }

  return new Promise((resolve, reject) => {
    currentUser?.getSession((err, session) => {
      if (err) {
        reject(err);
      } else {
        resolve(session);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export async function curryCognitoCreds() {
  let credentials;

  await getSession().then(async () => {
    const idToken = window.localStorage.getItem('idToken');

    const creds = new FromCognitoIdentityPool({
      client: new CognitoIdentityClient({
        region,
      }),
      identityPoolId,
      logins: {
        [identityProvider]: idToken,
      },
    });

    // FIX: MUST await the provider to get real credentials — creds() returns
    // a Promise (the AWS SDK v3 credential provider), not resolved
    // credentials. Without await, every S3 call (upload/list/view/delete)
    // ran with an unresolved Promise as `credentials` instead of real
    // accessKeyId/secretAccessKey/sessionToken, failing consistently.
    credentials = await creds();
  });

  return (fn) => (val, key) => fn(credentials, val, key);
}

export function getCurrentUser() {
  return currentUser;
}

function getCognitoUser(username) {
  const userData = {
    Username: username,
    Pool: userPool,
  };

  return new CognitoUser(userData);
}

export async function signUpUserWithEmail(username, email, password) {
  return new Promise((resolve, reject) => {
    const attributeList = [
      new CognitoUserAttribute({
        Name: 'email',
        Value: email,
      }),
    ];

    userPool.signUp(username, password, attributeList, [], (err, res) => {
      if (err) {
        reject(err);
      } else {
        resolve(res);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export async function signInWithEmail(username, password, navigate, callback) {
  return new Promise((resolve, reject) => {
    const authenticationData = {
      Username: username,
      Password: password,
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);

    currentUser = getCognitoUser(username);

    currentUser.authenticateUser(authenticationDetails, {
      onSuccess: (res) => {
        callback();
        resolve(res);
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: () => {
        navigate(routes.newPassword);
      },
    });
  }).catch((err) => {
    throw err;
  });
}

export function completeNewPasswordChallenge(password, attributesData) {
  return new Promise((resolve, reject) => {
    currentUser.completeNewPasswordChallenge(password, attributesData, {
      onSuccess: (res) => {
        resolve(res);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

export function forgotPassword(email, callback) {
  const userData = {
    Username: email,
    Pool: userPool,
  };

  const cognitoUser = new CognitoUser(userData);

  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: (res) => {
        resolve(res);
      },
      onFailure: (err) => {
        reject(err);
      },
      inputVerificationCode() {
        callback();
      },
    });
  });
}

export function confirmPassword(email, code, password) {
  const userData = {
    Username: email,
    Pool: userPool,
  };

  const cognitoUser = new CognitoUser(userData);

  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(code, password, {
      onSuccess: (res) => {
        resolve(res);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

export function signOut() {
  if (currentUser) {
    currentUser.signOut();
  }
}

export async function getAttributes() {
  return new Promise((resolve, reject) => {
    currentUser?.getUserAttributes((err, attributes) => {
      if (err) {
        reject(err);
      } else {
        resolve(attributes);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export async function setAttribute(attribute) {
  return new Promise((resolve, reject) => {
    const attributeList = [];
    const res = new CognitoUserAttribute(attribute);
    attributeList.push(res);

    currentUser?.updateAttributes(attributeList, (err, result) => {
      if (err) {
        reject(err);
      } else {
        resolve(result);
      }
    });
  }).catch((err) => {
    throw err;
  });
}
